import { watch } from 'vue'
import { normalizeTrackedPagePath } from '~/utils/pageViewStats'

export default defineNuxtPlugin(() => {
  if (typeof window === 'undefined') {
    return
  }

  const route = useRoute()
  let lastTrackedPath: string | null = null
  const trackedSessionStorageKey = 'kendrick:tracked-page-paths'
  const trackedPaths = new Set<string>()

  try {
    const storedPaths = window.sessionStorage.getItem(trackedSessionStorageKey)
    if (storedPaths) {
      const parsedPaths = JSON.parse(storedPaths)
      if (Array.isArray(parsedPaths)) {
        parsedPaths
          .filter((path): path is string => typeof path === 'string')
          .slice(-100)
          .forEach(path => trackedPaths.add(path))
      }
    }
  } catch {
    // Tracking must remain best-effort when storage is unavailable.
  }

  // Page views are queued and sent in one beacon when the page is hidden or closed, so a
  // whole session costs a single Netlify Function invocation instead of one per page.
  const MAX_PENDING_PATHS = 10
  const pendingPaths: string[] = []

  const flushPageViews = () => {
    if (!pendingPaths.length) {
      return
    }

    const payload = JSON.stringify({ paths: pendingPaths.splice(0, pendingPaths.length) })

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const sent = navigator.sendBeacon(
        '/api/page-views',
        new Blob([payload], { type: 'application/json' })
      )

      if (sent) {
        return
      }
    }

    void fetch('/api/page-views', {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: payload,
      keepalive: true,
      credentials: 'omit'
    }).catch(() => {})
  }

  const trackCurrentPath = () => {
    const normalizedPath = normalizeTrackedPagePath(route.path)
    if (!normalizedPath || normalizedPath === lastTrackedPath) {
      return
    }

    lastTrackedPath = normalizedPath

    // A page view is already represented once per session. Avoid generating a
    // paid Netlify Function invocation when visitors navigate back and forth
    // between pages they have already viewed.
    if (trackedPaths.has(normalizedPath)) {
      return
    }

    trackedPaths.add(normalizedPath)
    try {
      window.sessionStorage.setItem(
        trackedSessionStorageKey,
        JSON.stringify(Array.from(trackedPaths).slice(-100))
      )
    } catch {
      // Tracking remains best-effort when storage is unavailable.
    }

    pendingPaths.push(normalizedPath)
    if (pendingPaths.length >= MAX_PENDING_PATHS) {
      flushPageViews()
    }
  }

  // Automated browsers do not represent real visitors.
  if (navigator.webdriver) {
    return
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushPageViews()
    }
  })
  window.addEventListener('pagehide', flushPageViews)

  watch(() => route.path, () => {
    trackCurrentPath()
  }, { immediate: true })
})

